import React, { useState } from 'react';
import { ChevronDown, ChevronUp, LucideIcon } from 'lucide-react';

interface SectionHeaderProps {
  /** Title text to display */
  title: string;
  /** Optional icon component to display before the title */
  icon?: LucideIcon;
  /** Whether the section is collapsible */
  collapsible?: boolean;
  /** Initial collapsed state (only used when collapsible is true) */
  defaultCollapsed?: boolean;
  /** Controlled collapsed state (overrides internal state) */
  collapsed?: boolean;
  /** Callback when collapsed state changes */
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Children to render in the collapsible content area */
  children?: React.ReactNode;
  /** Additional class name for the header container */
  className?: string;
  /** Additional class name for the content container */
  contentClassName?: string;
  /** Optional right-side content (badges, buttons, etc.) */
  rightContent?: React.ReactNode;
}

/**
 * A reusable section header component with optional collapsible functionality.
 * Can be used in controlled or uncontrolled mode.
 */
const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  icon: Icon,
  collapsible = false,
  defaultCollapsed = false,
  collapsed: controlledCollapsed,
  onCollapsedChange,
  children,
  className = '',
  contentClassName = '',
  rightContent,
}) => {
  // Internal state for uncontrolled mode
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);

  // Use controlled or uncontrolled state
  const isCollapsed = controlledCollapsed !== undefined ? controlledCollapsed : internalCollapsed;

  const handleToggle = () => {
    const newCollapsed = !isCollapsed;

    // Update internal state if uncontrolled
    if (controlledCollapsed === undefined) {
      setInternalCollapsed(newCollapsed);
    }

    // Call callback if provided
    onCollapsedChange?.(newCollapsed);
  };

  const headerContent = (
    <div
      className={`flex items-center justify-between ${className} ${
        collapsible ? 'cursor-pointer select-none hover:bg-accent/50 rounded-lg -mx-2 px-2 py-1 transition-colors' : ''
      }`}
      onClick={collapsible ? handleToggle : undefined}
      role={collapsible ? 'button' : undefined}
      tabIndex={collapsible ? 0 : undefined}
      onKeyDown={collapsible ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggle();
        }
      } : undefined}
      aria-expanded={collapsible ? !isCollapsed : undefined}
      aria-label={collapsible ? (isCollapsed ? `Expand ${title}` : `Collapse ${title}`) : undefined}
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-5 w-5 text-primary" />}
        <h2 className="text-xl font-semibold text-foreground">{title}</h2>
      </div>
      <div className="flex items-center gap-2">
        {rightContent}
        {collapsible && (
          <span className="p-1">
            {isCollapsed ? (
              <ChevronDown className="h-5 w-5 text-foreground-muted" />
            ) : (
              <ChevronUp className="h-5 w-5 text-foreground-muted" />
            )}
          </span>
        )}
      </div>
    </div>
  );

  // If not collapsible, just render the header
  if (!collapsible) {
    return <div className="mb-4">{headerContent}</div>;
  }

  // Collapsible version with animated content
  return (
    <div>
      <div className="mb-2">{headerContent}</div>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'
        } ${contentClassName}`}
      >
        {children}
      </div>
    </div>
  );
};

export default SectionHeader;
